import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';
import './BoostModal.scss';

interface BoostModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  actionType: 'buy' | 'sell';
  buildingAddress?: string;
}

/**
 * Confirm dialog for the paid visibility boost, shared by every boost entry point.
 * Shows one inline error line when the request is refused so a failed boost is never
 * silent; the line is cleared on retry or close.
 */
const BoostModal: React.FC<BoostModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  actionType,
  buildingAddress,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  if (!isOpen) return null;

  /** Sends the boost through the caller; closes on success, shows the error line on any rejection. */
  const handleConfirm = async () => {
    setIsLoading(true);
    setHasError(false);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      console.error('Error confirming boost:', error);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  /** Closes the modal and drops the error line so the next open starts clean. */
  const handleCancel = () => {
    setHasError(false);
    onClose();
  };

  return ReactDOM.createPortal(
    <div className="boost-modal-overlay">
      <div className="boost-modal">
        <div className="boost-modal__gradient-bg">
          <div className="boost-modal__content">
            <div className="boost-modal__rocket-wrapper">
              <div className="boost-modal__rocket">
                <span className="boost-modal__rocket-emoji">🚀</span>
              </div>
            </div>

            <h2 className="boost-modal__title">
              <Trans i18nKey={`boostModal.${actionType}Title`} />
            </h2>

            <p className="boost-modal__description">
              <Trans 
                i18nKey={`boostModal.description.${actionType}`}
                values={{ address: buildingAddress }}
              />
            </p>

            <div className="boost-modal__info">
              <p className="boost-modal__info-text">
                <Trans i18nKey="boostModal.emailInfo" />
              </p>
            </div>

            {hasError && (
              <p className="boost-modal__error" role="alert">
                {t('boostModal.error')}
              </p>
            )}
          </div>

          <div className="boost-modal__footer">
            <button
              className="boost-modal__button boost-modal__button--cancel"
              onClick={handleCancel}
              disabled={isLoading}
            >
              <Trans i18nKey="boostModal.cancelButton" />
            </button>
            <button
              className="boost-modal__button boost-modal__button--confirm"
              onClick={handleConfirm}
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="boost-modal__spinner"></div>
              ) : (
                <Trans i18nKey="boostModal.confirmButton" />
              )}
            </button>
          </div>

          <p className="boost-modal__no-commitment">
            <Trans i18nKey="boostModal.noCommitment" />
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BoostModal;
